import React from 'react'
import { View } from 'react-native'
import styled from 'styled-components/native'

import { Flex } from '../../../components/FlexBox'
import { Text } from '../../../components/common/Text'
import useTranslation from '../../../hooks/useTranslation'
import SmallButton from './SmallButton'

const Bar = styled(View)`
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  padding: 12px;
  border-top-width: 1px;
  border-top-color: ${props => props.theme.colors.lightGrey};
  background-color: ${props => props.theme.colors.appBackground};
`

interface PublishBarProps {
  changeCount: number
  publishing: boolean
  /**
   * Why publishing is unavailable. Blocks Publish and replaces the pending
   * count, so the bar always answers the question a disabled button raises.
   * Discard stays live -- backing the draft out is how you clear the block.
   */
  blockedReason?: string | null
  onPublish: () => void
  onDiscard: () => void
}

/**
 * Edits are held in the client until published -- there is no server-side
 * draft, so this bar is the only signal that work is pending and would be lost
 * on a reload. It stays visible when clean rather than appearing on first edit,
 * so its position is predictable.
 */
export const PublishBar = ({
  changeCount,
  publishing,
  blockedReason,
  onPublish,
  onDiscard
}: PublishBarProps) => {
  const { t } = useTranslation()
  const isDirty = changeCount > 0

  return (
    <Bar>
      {/* Spacer, so the status sits beside Discard rather than across the bar --
          the count describes what those buttons act on. */}
      <Flex flex={1} />
      <Flex flexDirection="row" alignItems="center">
        {/* The reason outranks the count: "1 unpublished change" next to a
            dead Publish button reads as a bug rather than a rule. */}
        <Text
          styles={{
            fontSize: 13,
            marginRight: 12,
            ...(blockedReason ? { color: '#ED435C' } : {})
          }}
        >
          {blockedReason ||
            (isDirty
              ? t('contentCuration:publish:pending', { count: changeCount })
              : t('contentCuration:publish:clean'))}
        </Text>
        <SmallButton
          label={t('contentCuration:publish:discard')}
          onPress={onDiscard}
          disabled={!isDirty || publishing}
        />
        <Flex ml={2}>
          <SmallButton
            label={t('contentCuration:publish:publish')}
            onPress={onPublish}
            disabled={!isDirty || publishing || !!blockedReason}
            variant="primary"
          />
        </Flex>
      </Flex>
    </Bar>
  )
}

export default PublishBar
